/**
 * Shipping countdown custom element.
 * Displays a countdown to the same-day shipping cutoff.
 * Hidden on excluded weekdays, listed holidays, and outside the lead-time window.
 */

class ShippingCountdown extends HTMLElement {
  /** @type {number | null} */
  #intervalId = null;

  connectedCallback() {
    this.#update();
    this.#scheduleNext();
  }

  disconnectedCallback() {
    if (this.#intervalId) {
      clearTimeout(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #scheduleNext() {
    this.#intervalId = setTimeout(() => {
      this.#update();
      this.#scheduleNext();
    }, 1000);
  }

  #update() {
    const remaining = this.#getRemainingMs();
    if (remaining === null || remaining <= 0) {
      this.style.display = 'none';
      return;
    }

    const totalSeconds = Math.ceil(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    /** @type {HTMLElement | null} */
    const el = this.querySelector('[data-countdown]');
    if (!el) return;

    if (hours > 0) {
      el.textContent = `${hours} Std. ${minutes} Min. ${seconds} Sek.`;
    } else if (minutes > 0) {
      el.textContent = `${minutes} Min. ${seconds} Sek.`;
    } else {
      el.textContent = `${seconds} Sek.`;
    }

    const URGENT_THRESHOLD_MS = 30 * 60_000;
    this.toggleAttribute('data-urgent', remaining < URGENT_THRESHOLD_MS);

    this.style.display = '';
  }

  /**
   * @returns {number | null} ms until cutoff, or null if countdown should be hidden
   */
  #getRemainingMs() {
    const tz = this.dataset.timezone || 'Europe/Berlin';
    const now = new Date();
    const local = new Date(now.toLocaleString('en-US', { timeZone: tz }));

    const day = local.getDay(); // 0=Sun … 6=Sat

    // Always exclude Sunday (0)
    if (day === 0) return null;

    // Configurable weekday exclusions
    const excludeDays = this.dataset.excludeDays || '';
    if (excludeDays.split(',').map(Number).includes(day)) return null;

    // Holiday check
    const holidays = this.dataset.holidays || '';
    if (holidays) {
      const today = this.#fmtDate(local);
      const list = holidays.split(',').map((d) => d.trim());
      if (list.includes(today)) return null;
    }

    const cutoffHour = parseInt(this.dataset.cutoffHour || '10', 10);
    const cutoffMinute = parseInt(this.dataset.cutoffMinute || '0', 10);
    const leadHours = parseInt(this.dataset.leadHours || '3', 10);

    const nowMins = local.getHours() * 60 + local.getMinutes();
    const cutoffMins = cutoffHour * 60 + cutoffMinute;
    const startMins = cutoffMins - leadHours * 60;

    if (nowMins < startMins || nowMins >= cutoffMins) return null;

    return (cutoffMins - nowMins) * 60_000 - local.getSeconds() * 1000;
  }

  /**
   * @param {Date} d
   * @returns {string} YYYY-MM-DD
   */
  #fmtDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}

if (!customElements.get('shipping-countdown')) {
  customElements.define('shipping-countdown', ShippingCountdown);
}